import {
    type FC,
    createContext,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef,
    type ReactNode,
} from 'react';
import serverVars from 'server-vars';

import { MOBILE, type DEVICE_TYPES, isDeviceType } from 'dibs-buyer-layout/exports/deviceTypeEnums';

import styles from './useDeviceType.scss';

type DeviceTypeContext = { deviceType: DEVICE_TYPES; isMobile: boolean };

const DEV_BUILD = process.env.NODE_ENV !== 'production';

function assertIsDeviceType(deviceType: unknown): asserts deviceType is DEVICE_TYPES {
    if (DEV_BUILD && !isDeviceType(deviceType)) {
        throw Error(`deviceType "${String(deviceType)}" does not satisfy DEVICE_TYPES.`);
    }
}

function assertIsDeviceTypeContext(
    context: DeviceTypeContext | null
): asserts context is DeviceTypeContext {
    if (DEV_BUILD && typeof context === null)
        throw Error(
            'DeviceTypeContext is null. Is there a DeviceTypeProvider with an initial value?'
        );
}

const deviceTypeContext = createContext<DeviceTypeContext | null>(null);

export const useDeviceType = (): DeviceTypeContext => {
    const context = useContext(deviceTypeContext);
    assertIsDeviceTypeContext(context);
    return context;
};

const root = typeof window !== 'undefined' ? document.getElementById('js-root') : null;

export const DeviceTypeProvider: FC<{ serverDeviceType?: DEVICE_TYPES; children: ReactNode }> = ({
    serverDeviceType,
    children,
}) => {
    const previousContext = useContext(deviceTypeContext);
    if (previousContext) {
        // eslint-disable-next-line no-console
        console.error('DeviceTypeProvider already exists.');
    } else if (typeof window === 'undefined') {
        if (!serverDeviceType) {
            // eslint-disable-next-line no-console
            console.error('DeviceTypeProvider requires a serverDeviceType on the server.');
        }
    } else if (serverDeviceType) {
        // eslint-disable-next-line no-console
        console.error('DeviceTypeProvider should not have serverDeviceType prop on the client.');
    }
    const clientDeviceType = serverVars.get('deviceType');
    const contextDeviceType = serverDeviceType || clientDeviceType;
    assertIsDeviceType(contextDeviceType);

    const [deviceType, setDeviceType] = useState(contextDeviceType);
    const timeoutRef = useRef(0 as unknown as NodeJS.Timeout);

    const resizeListener = useCallback(() => {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
            if (root) {
                const responsiveDeviceType = getComputedStyle(root)
                    .getPropertyValue('--nftLandingResponsive')
                    .trim();
                assertIsDeviceType(responsiveDeviceType);
                setDeviceType(responsiveDeviceType);
            }
        }, 1 / 15);
    }, []);

    useEffect(() => {
        root?.classList.add(styles.deviceType);
        window.addEventListener('resize', resizeListener, { passive: true });
    }, [resizeListener]);

    return (
        <deviceTypeContext.Provider value={{ deviceType, isMobile: deviceType === MOBILE }}>
            {children}
        </deviceTypeContext.Provider>
    );
};
